import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import {
  Clock,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Headphones,
  Loader2,
  Sparkles,
  XCircle,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  NotificationCard,
  StatusBadge,
} from '@afrilink/ui';
import { supabase } from '@/shared/lib/supabase';

/**
 * DT-1 customer processing page (2026-05-11).
 *
 * Route: /app/domains/intent/:ticket_id
 *
 * Renders the ticket's current workflow_state and (when applicable) the
 * linked invoice for one-click payment. Polls every 30 seconds, pausing
 * when the tab is hidden (Page Visibility API) per locked decision G3-Q7.
 *
 * Customer never sees technical errors or balance warnings here — the EF
 * filters those into internal ticket notes for staff. This page only
 * surfaces the friendly customer-visible state.
 */

type WorkflowState =
  | 'open'
  | 'awaiting_payment'
  | 'pending_staff_action'
  | 'executing'
  | 'execution_succeeded'
  | 'execution_failed'
  | 'cancelled_by_customer'
  | 'cancelled_by_staff'
  | 'resolved';

interface TicketRow {
  id: string;
  number: string;
  subject: string;
  status: string;
  workflow_state: { state: WorkflowState; reason?: string; transitioned_at?: string } | null;
  metadata: { operation?: string; domain_name?: string; period_years?: number } | null;
  created_at: string;
  closed_at: string | null;
}

interface InvoiceRow {
  id: string;
  number: string;
  total: number;
  lifecycle_state: string;
}

const POLL_INTERVAL_MS = 30_000;

export default function DomainIntentProcessingPage() {
  const navigate = useNavigate();
  const { ticket_id: ticketId } = useParams<{ ticket_id: string }>();

  const [ticket, setTicket] = useState<TicketRow | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchState = useCallback(async () => {
    if (!ticketId) return;
    try {
      const [tRes, iRes] = await Promise.all([
        supabase
          .from('ticket')
          .select('id, number, subject, status, workflow_state, metadata, created_at, closed_at')
          .eq('id', ticketId)
          .maybeSingle(),
        supabase
          .from('invoice')
          .select('id, number, total, lifecycle_state')
          .eq('intent_ticket_id', ticketId)
          .maybeSingle(),
      ]);
      if (tRes.error) throw tRes.error;
      if (!tRes.data) {
        setError('not_found');
        setLoading(false);
        return;
      }
      setTicket(tRes.data as unknown as TicketRow);
      setInvoice((iRes.data as unknown as InvoiceRow | null) ?? null);
      setError(null);
      setLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setLoading(false);
    }
  }, [ticketId]);

  // Initial fetch + polling (pauses when tab hidden)
  useEffect(() => {
    fetchState();
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      stop();
      interval = setInterval(fetchState, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        fetchState();
        start();
      }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchState]);

  const operationLabel = operationLabelFor(ticket?.metadata?.operation);
  const domainName = ticket?.metadata?.domain_name ?? ticket?.subject ?? 'your domain';
  const uuidShort = ticket?.id?.substring(0, 8) ?? '';

  return (
    <UniversalPage
      title={`${operationLabel}: ${domainName}`}
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Domains', href: '/app/domains' },
        { label: `Processing #${uuidShort}` },
      ]}
    >
      {loading && (
        <Card interactive={false}>
          <UniversalLoadingState variant="spinner" caption="Loading your request…" />
        </Card>
      )}

      {!loading && error === 'not_found' && (
        <NotificationCard
          tone="danger"
          title="We could not find this request"
          body={
            <>
              The reference may be wrong or the request may belong to a different account.
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/app/domains')}>
                  Back to domains
                </Button>
              </div>
            </>
          }
        />
      )}

      {!loading && error && error !== 'not_found' && (
        <NotificationCard
          tone="danger"
          title="Could not load this request"
          body={
            <>
              {error}
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => fetchState()}>
                  Try again
                </Button>
              </div>
            </>
          }
        />
      )}

      {!loading && ticket && (
        <ProcessingCard ticket={ticket} invoice={invoice} onPay={(invId) => navigate(`/app/billing/invoices/${invId}`)} />
      )}

      {ticket && (
        <Card interactive={false} className="mt-4 text-xs text-muted-foreground">
          <div className="px-5 py-3">
            Reference: <span className="font-mono tabular-nums">#{uuidShort}</span>
            {' · '}
            Ticket {ticket.number}
            {' · '}
            <Link to={`/app/support/${ticket.id}`} className="text-primary hover:underline">
              View full ticket
            </Link>
          </div>
        </Card>
      )}
    </UniversalPage>
  );
}

function operationLabelFor(op: string | undefined): string {
  switch (op) {
    case 'register':    return 'Domain registration';
    case 'renew':       return 'Domain renewal';
    case 'transfer_in': return 'Domain transfer-in';
    default:            return 'Domain request';
  }
}

interface ProcessingCardProps {
  ticket: TicketRow;
  invoice: InvoiceRow | null;
  onPay: (invoiceId: string) => void;
}

function ProcessingCard({ ticket, invoice, onPay }: ProcessingCardProps) {
  const state: WorkflowState = (ticket.workflow_state?.state as WorkflowState | undefined) ?? 'open';
  const businessHours = isSastBusinessHours();
  const slaText = businessHours
    ? 'A support agent will contact you within the hour.'
    : 'A support agent will respond first thing the next business day (07:00 SAST, Mon-Fri).';
  const invoicePaid = invoice?.lifecycle_state === 'paid';

  // Awaiting payment + unpaid invoice → show pay button
  if (state === 'awaiting_payment' && invoice && !invoicePaid) {
    return (
      <Card interactive={false}>
        <header className="flex items-center gap-3 border-b border-border bg-primary/5 px-6 py-4">
          <CreditCard className="h-5 w-5 text-primary" />
          <div>
            <p className="text-base font-semibold text-navy-primary">Payment required</p>
            <p className="text-sm text-muted-foreground">Pay the invoice to start processing.</p>
          </div>
        </header>
        <div className="space-y-4 p-6">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Amount due</span>
            <span className="text-2xl font-semibold tabular-nums text-navy-primary">
              R{invoice.total.toFixed(2)}
            </span>
          </div>
          <p className="text-sm text-foreground-soft">
            Once paid, our team will begin processing your request. {slaText}
          </p>
          <Button size="lg" className="w-full" onClick={() => onPay(invoice.id)}>
            <CreditCard className="size-4" />
            Pay R{invoice.total.toFixed(2)} now
          </Button>
        </div>
      </Card>
    );
  }

  // Awaiting payment + no invoice yet (rare race)
  if (state === 'awaiting_payment') {
    return (
      <PendingCard
        icon={<Loader2 className="h-5 w-5 animate-spin text-primary" />}
        title="Preparing your invoice"
        body="One moment — we are finishing up. This page refreshes automatically."
      />
    );
  }

  // Paid / pending staff action — staff queue
  if (state === 'pending_staff_action' || invoicePaid) {
    return (
      <PendingCard
        icon={<Headphones className="h-5 w-5 text-primary" />}
        title="Payment received — we are on it"
        tone="success"
        body={
          <>
            Thank you. {slaText} You will get an email confirmation when registration is complete.
            <StatusBadgeInline tone="active">
              {businessHours ? 'In business hours' : 'After hours'}
            </StatusBadgeInline>
          </>
        }
      />
    );
  }

  if (state === 'executing') {
    return (
      <PendingCard
        icon={<Loader2 className="h-5 w-5 animate-spin text-primary" />}
        title="Registering now"
        body="Our team is actioning your request with the registry. This usually takes a minute or two."
      />
    );
  }

  if (state === 'execution_succeeded' || state === 'resolved') {
    return (
      <Card interactive={false}>
        <header className="flex items-center gap-3 border-b border-border bg-success/5 px-6 py-4">
          <CheckCircle2 className="h-5 w-5 text-success" />
          <div>
            <p className="text-base font-semibold text-navy-primary">All done</p>
            <p className="text-sm text-success">Your request has been completed successfully.</p>
          </div>
        </header>
        <div className="space-y-4 p-6">
          <p className="text-sm text-foreground-soft">
            <Sparkles className="mr-2 inline-block h-4 w-4 text-success" />
            You should see your domain in the list shortly.
          </p>
          <div className="flex gap-2">
            <Link to="/app/domains">
              <Button variant="outline">Back to domains</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  if (state === 'execution_failed') {
    return (
      <Card interactive={false}>
        <header className="flex items-center gap-3 border-b border-border bg-danger/5 px-6 py-4">
          <AlertCircle className="h-5 w-5 text-danger" />
          <div>
            <p className="text-base font-semibold text-navy-primary">We hit a problem</p>
            <p className="text-sm text-danger">Our team is investigating and will be in touch.</p>
          </div>
        </header>
        <div className="space-y-4 p-6">
          <p className="text-sm text-foreground-soft">
            We will email you with next steps. If you need to reach us in the meantime, reply on
            the ticket below or email <strong>support@sighthost.online</strong>.
          </p>
          <Link to={`/app/support/${ticket.id}`}>
            <Button variant="outline">
              <Headphones className="size-4" />
              Open ticket
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (state === 'cancelled_by_customer' || state === 'cancelled_by_staff') {
    return (
      <Card interactive={false}>
        <header className="flex items-center gap-3 border-b border-border bg-card-subtle px-6 py-4">
          <XCircle className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="text-base font-semibold text-navy-primary">Request cancelled</p>
            <p className="text-sm text-muted-foreground">
              {ticket.workflow_state?.reason
                ? `Reason: ${ticket.workflow_state.reason.replace(/_/g, ' ')}`
                : 'This request was cancelled.'}
            </p>
          </div>
        </header>
        <div className="p-6">
          <Link to="/app/domains">
            <Button variant="outline">Back to domains</Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Fallback (state = 'open' or unknown)
  return (
    <PendingCard
      icon={<Clock className="h-5 w-5 text-primary" />}
      title="Request received"
      body={slaText}
    />
  );
}

interface PendingCardProps {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  tone?: 'default' | 'success';
}

function PendingCard({ icon, title, body, tone = 'default' }: PendingCardProps) {
  const bg = tone === 'success' ? 'bg-success/5' : 'bg-primary/5';
  return (
    <Card interactive={false}>
      <header className={`flex items-center gap-3 border-b border-border ${bg} px-6 py-4`}>
        {icon}
        <p className="text-base font-semibold text-navy-primary">{title}</p>
      </header>
      <div className="space-y-3 p-6 text-sm text-foreground-soft">{body}</div>
    </Card>
  );
}

function StatusBadgeInline({ tone, children }: { tone: 'active' | 'pending'; children: React.ReactNode }) {
  return (
    <span className="ml-2 inline-block">
      <StatusBadge tone={tone}>{children}</StatusBadge>
    </span>
  );
}

function isSastBusinessHours(date = new Date()): boolean {
  const sast = new Date(date.getTime() + 2 * 60 * 60 * 1000);
  const day = sast.getUTCDay();
  const hour = sast.getUTCHours();
  if (day === 0 || day === 6) return false;
  return hour >= 7 && hour < 17;
}
